import React from 'react';
import { fadeIn } from '../../utils/cssAnimationsUtils';

import { useTheme, Box, useBoolean, HStack } from '@wegroup/design-system';
import LeafletGoogleMap, { LeafletGoogleMapProps } from './LeafletGoogleMap';
import MapButtons from '../static-map/MapButtons';
import MapSkeleton from '../static-map/MapSkeleton';
import LeafletZoomButtons from './LeafletZoomButtons';
import { LatLngLiteral } from 'leaflet';

interface Props {
  centerCoords: { lat: number; lng: number };
  centerAlignment?: 'left' | 'right' | 'center';
  markerCoords?: LatLngLiteral[][];
  isSatelliteDefault?: boolean;
  withOptionsMenu?: boolean;
  withZoomControls?: boolean;
  isLoading?: boolean;
}

const LeafletMap: React.FC<
  React.PropsWithChildren<Props & LeafletGoogleMapProps>
> = ({
  children,
  isSatelliteDefault,
  withOptionsMenu = false,
  withZoomControls = false,
  isLoading,
  ...otherProps
}) => {
  const { colors } = useTheme();
  const [isSatelliteView, setIsSatelliteView] = useBoolean(isSatelliteDefault);

  if (isLoading) {
    return <MapSkeleton color={colors.gray.strokeAndBg} />;
  }

  return (
    <Box
      position="relative"
      w="100%"
      h="100%"
      animation={`${fadeIn} 2s`}
      data-test-id="Common_LeafletMap"
    >
      <LeafletGoogleMap
        mapType={isSatelliteView ? 'SATELLITE' : 'STREETS'}
        {...otherProps}
      >
        <HStack
          // Leaflet map has a z-index of 400
          position="absolute"
          zIndex="401"
          w="100%"
          h="100%"
          p="2"
          justifyContent="flex-end"
          alignItems="flex-start"
          spacing="2"
        >
          {withZoomControls && <LeafletZoomButtons />}
          {withOptionsMenu && (
            <MapButtons
              isSatelliteView={isSatelliteView}
              onSatelliteClick={setIsSatelliteView.on}
              onRoadmapClick={setIsSatelliteView.off}
            />
          )}
        </HStack>
      </LeafletGoogleMap>
    </Box>
  );
};

export default LeafletMap;
